/**
 * Vocabulário de tela da campanha — o que cada estado SIGNIFICA para quem opera.
 *
 * Separado da tela pelo mesmo motivo de `group-ui.ts`: o teste afirma sobre os rótulos sem
 * montar a página, e lista, cartão e diálogo usam o mesmo texto.
 *
 * Nada aqui decide regra de negócio. O servidor já mandou `status`, a contagem de grupos e
 * o limite do plano avaliado. Isto é tradução, não cálculo (invariantes 3 e 4).
 */
import type { Campaign, CampaignGroup, MoneyExposureView } from '../../lib/api';

export type CampaignStatus = Campaign['status'];

/** Os três estados, na ordem em que contam a história da campanha. */
export const CAMPAIGN_STATUSES: readonly CampaignStatus[] = ['ACTIVE', 'PAUSED', 'ARCHIVED'];

export interface CampaignStatusSpec {
  label: string;
  tone: 'success' | 'warning' | 'neutral';
  hint: string;
}

/**
 * `PAUSED` e `ARCHIVED` parecem a mesma coisa de longe e não são: pausar é "volto amanhã",
 * arquivar é "saiu da operação". A frase precisa dizer o que acontece com o LINK, que é a
 * única pergunta prática de quem está na tela.
 */
export const CAMPAIGN_STATUS: Record<CampaignStatus, CampaignStatusSpec> = {
  ACTIVE: {
    label: 'Distribuindo',
    tone: 'success',
    hint: 'A campanha está no ar e manda gente para os grupos vinculados.',
  },
  PAUSED: {
    label: 'Pausada',
    tone: 'warning',
    hint: 'Para de mandar gente nova. Os grupos continuam registrando entrada e saída.',
  },
  ARCHIVED: {
    label: 'Arquivada',
    tone: 'neutral',
    hint: 'Fora da operação. O histórico continua guardado e dá para desarquivar.',
  },
};

export function campaignStatusLabel(status: CampaignStatus): string {
  return CAMPAIGN_STATUS[status].label;
}

/**
 * Frase da contagem de grupos.
 *
 * `0` aqui é **mesmo zero** — a campanha existe e não tem nenhum grupo vinculado —, e não
 * "não sei". Por isso o texto é afirmativo e vem com o que fazer a respeito: uma campanha
 * sem grupo não distribui nada, e essa é a causa número um de "criei e não funcionou".
 */
export function groupsSummary(campaign: Pick<Campaign, 'groupsCount'>): string {
  if (campaign.groupsCount === 0) return 'Nenhum grupo vinculado';
  return campaign.groupsCount === 1 ? '1 grupo' : `${campaign.groupsCount} grupos`;
}

/** `true` quando a campanha está no ar mas não tem para onde mandar ninguém. */
export function isDistributingWithoutGroups(campaign: Pick<Campaign, 'status' | 'groupsCount'>) {
  return campaign.status === 'ACTIVE' && campaign.groupsCount === 0;
}

/**
 * Nome do grupo dentro da campanha.
 *
 * Mesma regra de `group-ui.ts`: `null` é "o WhatsApp não nos disse o nome", não "grupo sem
 * nome" — por isso a frase explica, em vez de um travessão solto (invariante 8).
 */
export const GROUP_WITHOUT_NAME = 'Sem nome no WhatsApp';

export function campaignGroupName(group: Pick<CampaignGroup, 'name'>): string {
  return group.name ?? GROUP_WITHOUT_NAME;
}

export interface GroupEligibility {
  eligible: boolean;
  label: string;
  tone: 'success' | 'warning' | 'neutral';
}

/**
 * Se o grupo pode receber gente **agora**, do ponto de vista da campanha.
 *
 * A mesma decisão existe no servidor (`listEligibleGroups`), e é de propósito que a daqui
 * seja só um RÓTULO: a distribuição de verdade é decidida lá. O que esta função evita é a
 * tela mostrar "3 grupos" numa campanha em que dois estão cheios e um foi arquivado — o
 * número certo pela conta errada, que é como se descobre o problema tarde demais.
 */
export function groupEligibility(
  group: Pick<CampaignGroup, 'status' | 'isActive' | 'archivedAt'>,
): GroupEligibility {
  if (group.archivedAt !== null) {
    return { eligible: false, label: 'Arquivado', tone: 'neutral' };
  }
  if (group.status === 'FULL') {
    return { eligible: false, label: 'Cheio', tone: 'warning' };
  }
  if (!group.isActive) {
    return { eligible: false, label: 'Pausado', tone: 'neutral' };
  }
  return { eligible: true, label: 'Recebendo', tone: 'success' };
}

/** Quantos dos grupos vinculados podem receber gente agora. */
export function countEligible(groups: readonly CampaignGroup[]): number {
  return groups.filter((group) => groupEligibility(group).eligible).length;
}

/* -------------------------------------------------------------------------- */
/*  A exposição ao dinheiro                                                    */
/* -------------------------------------------------------------------------- */

export type MoneyExposureLevel = MoneyExposureView['level'];

export interface MoneyExposureSpec {
  label: string;
  tone: 'success' | 'warning' | 'danger' | 'neutral';
  hint: string;
}

/**
 * Como cada nível da escala aparece na tela.
 *
 * ⚠️ **Tradução, nunca cálculo.** Quem decide o nível é o servidor
 * (`packages/db/src/money-exposure.ts`), porque é o mesmo número que decide a severidade dos
 * incidentes — refazer a conta aqui produziria a segunda verdade que discorda da primeira no
 * primeiro caminho esquecido (invariante 3).
 *
 * Os rótulos não usam a palavra "orgânico" de propósito, pelo mesmo motivo do servidor: o
 * clique pago que chega sem marcação (botão estático na landing page) cairia nesse nível, e
 * chamar isso de orgânico seria a tela afirmando algo que ninguém apurou.
 */
export const MONEY_EXPOSURE: Record<MoneyExposureLevel, MoneyExposureSpec> = {
  NO_EXPOSURE: {
    label: 'Sem verba',
    tone: 'neutral',
    hint: 'Nenhuma campanha viva usa este caminho. Ficar sem receber lead por um tempo é normal.',
  },
  NO_PAID_EVIDENCE: {
    label: 'Sem tráfego pago visto',
    tone: 'neutral',
    hint: 'A campanha está viva, mas não encontramos clique de anúncio nas últimas 24 horas.',
  },
  PAID_OBSERVED: {
    label: 'Tráfego pago',
    tone: 'warning',
    hint: 'Cliques de anúncio chegaram por aqui nas últimas 24 horas — há verba pagando este caminho.',
  },
  PAID_ACTIVE: {
    label: 'Anúncio gastando',
    tone: 'danger',
    hint: 'Há campanha do Meta declarada com dinheiro saindo agora. Problema aqui é prejuízo por minuto.',
  },
};

/** A campanha tem uma campanha do Meta declarada? */
export function hasMetaLink(campaign: Pick<Campaign, 'metaCampaignsCount'>): boolean {
  return campaign.metaCampaignsCount > 0;
}

/**
 * Como a ligação foi feita, em português.
 *
 * A distinção aparece na tela porque ela responde "isso é fato ou palpite?" — e quem abrir a
 * gaveta daqui a três meses, com a ligação errada, precisa saber por onde começar: `MANUAL`
 * errado é id copiado errado; `Confirmada` errada é clique vindo misturado.
 */
export function metaLinkSourceLabel(source: 'MANUAL' | 'SUGGESTED' | null): string {
  if (source === 'SUGGESTED') return 'Confirmada a partir dos cliques';
  if (source === 'MANUAL') return 'Escolhida à mão';
  return 'Sem ligação';
}

/**
 * A força da sugestão, em porcentagem inteira.
 *
 * Inteira e não com casas: `91,7%` sugere uma precisão que uma contagem de cliques de sete
 * dias não tem, e ninguém decide diferente entre 91% e 92%.
 */
export function suggestionShareLabel(share: number): string {
  return `${Math.round(share * 100)}%`;
}
